/**
 * FileFieldControl — file/image field control backed by Emergent Object Storage.
 *
 * Stores the file id in the form data and renders a preview (image inline,
 * file as a download chip). 5 MB hard cap (enforced both client- and server-side).
 */
import { useEffect, useRef, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Paperclip, Trash, X, ImageSquare, FileText, ArrowSquareOut, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_BYTES = 5 * 1024 * 1024;
const BASE = process.env.REACT_APP_BACKEND_URL;

function humanSize(b) {
    if (!b) return "0 B";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

function ImagePreview({ fileId }) {
    const [src, setSrc] = useState(null);
    useEffect(() => {
        let revoke;
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get(`/files/${fileId}`, { responseType: "blob" });
                if (cancelled) return;
                const url = URL.createObjectURL(res.data);
                revoke = url;
                setSrc(url);
            } catch { /* unauthorised or missing — leave preview empty */ }
        })();
        return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
    }, [fileId]);
    if (!src) {
        return <div className="bg-secondary/50 border border-border rounded-sm h-32 flex items-center justify-center text-xs text-muted-foreground">Loading…</div>;
    }
    return <img src={src} alt="" className="rounded-sm border border-border max-h-48 object-contain bg-secondary/30" />;
}

export default function FileFieldControl({
    fieldName,
    isImage = false,
    value,
    onChange,
    disabled = false,
    testid,
}) {
    const inputRef = useRef(null);
    const [meta, setMeta] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileId = typeof value === "string" && value.startsWith("file:") ? value.slice(5)
        : typeof value === "object" && value?.id ? value.id : null;

    useEffect(() => {
        let cancel = false;
        (async () => {
            if (!fileId) { setMeta(null); return; }
            try {
                const { data } = await api.get(`/files/${fileId}/meta`);
                if (!cancel) setMeta(data);
            } catch { if (!cancel) setMeta(null); }
        })();
        return () => { cancel = true; };
    }, [fileId]);

    const pick = () => inputRef.current?.click();

    const upload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting same file later
        if (!file) return;
        if (file.size > MAX_BYTES) {
            toast.error(`File too large. Max 5 MB; this file is ${humanSize(file.size)}.`);
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/files/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(`file:${data.id}`);
            setMeta({ ...data, filename: data.filename, size_bytes: data.size_bytes });
            toast.success("File uploaded");
        } catch (err) { toast.error(extractError(err)); }
        finally { setUploading(false); }
    };

    const clearFile = async () => {
        if (!fileId) { onChange(""); return; }
        try { await api.delete(`/files/${fileId}`); }
        catch (err) { console.warn("[file-field] delete failed:", err); }
        onChange("");
        setMeta(null);
    };

    return (
        <div className="border border-dashed border-border rounded-sm p-3 space-y-2 bg-secondary/20" data-testid={testid}>
            <input ref={inputRef} type="file" hidden onChange={upload}
                accept={isImage ? "image/*" : "image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,application/json"} />

            {!fileId && (
                <Button type="button" variant="outline" size="sm" disabled={disabled || uploading} onClick={pick} className="rounded-sm w-full justify-center" data-testid={`${testid}-upload-btn`}>
                    <UploadSimple size={14} className="mr-2" />
                    {uploading ? "Uploading…" : isImage ? "Choose image" : "Choose file"}
                </Button>
            )}

            {fileId && meta && (
                <div className="space-y-2" data-testid={`${testid}-attached`}>
                    {isImage || (meta.content_type || "").startsWith("image/") ? (
                        <ImagePreview fileId={fileId} />
                    ) : null}
                    <div className="flex items-center justify-between gap-2 bg-card border border-border rounded-sm px-2 py-1.5">
                        <div className="flex items-center gap-2 min-w-0 text-xs">
                            {((meta.content_type || "").startsWith("image/") || isImage) ? <ImageSquare size={14} /> : <FileText size={14} />}
                            <span className="font-medium truncate" title={meta.filename}>{meta.filename}</span>
                            <span className="font-mono text-[10px] text-muted-foreground">{humanSize(meta.size_bytes)}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                            <a href={`${BASE}/api/files/${fileId}`} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground p-1" title="Open in new tab" data-testid={`${testid}-open`}>
                                <ArrowSquareOut size={13} />
                            </a>
                            {!disabled && (
                                <button type="button" onClick={clearFile} className="text-muted-foreground hover:text-destructive p-1" title="Remove" data-testid={`${testid}-remove`}>
                                    <X size={13} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {fileId && !meta && (
                <div className="flex items-center justify-between gap-2 bg-card border border-border rounded-sm px-2 py-1.5 text-xs">
                    <span className="flex items-center gap-2"><Paperclip size={12} /> File attached</span>
                    {!disabled && (
                        <button type="button" onClick={clearFile} className="text-muted-foreground hover:text-destructive" data-testid={`${testid}-remove`}>
                            <Trash size={12} />
                        </button>
                    )}
                </div>
            )}
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                Max 5 MB · {isImage ? "Images only" : "Images & standard documents (PDF, Word, Excel, etc.)"}
            </p>
        </div>
    );
}
